/**
 * Ascension Theme — Core JavaScript
 * Scroll animations, reduced motion, parallax hooks
 */

(function () {
  'use strict';

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /**
   * Intersection Observer for scroll-triggered animations
   */
  function initScrollAnimations() {
    if (prefersReducedMotion) return;

    const elements = document.querySelectorAll('[data-animate]');
    if (!elements.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.15,
        rootMargin: '0px 0px -40px 0px',
      }
    );

    elements.forEach((el) => {
      observer.observe(el);

      const rect = el.getBoundingClientRect();
      const visibleHeight = Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0);
      if (visibleHeight > 0 && visibleHeight / rect.height >= 0.08) {
        el.classList.add('is-visible');
        observer.unobserve(el);
      }
    });
  }

  /**
   * Subtle parallax for elements with data-parallax
   */
  function initParallax() {
    if (prefersReducedMotion) return;

    const elements = document.querySelectorAll('[data-parallax]');
    if (!elements.length) return;

    let ticking = false;

    function updateParallax() {
      elements.forEach((el) => {
        const rect = el.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const ratio = parseFloat(el.dataset.parallax) || 0.15;
        const offset = (rect.top - viewportHeight / 2) * ratio;
        el.style.transform = `translateY(${offset}px)`;
      });
      ticking = false;
    }

    function onScroll() {
      if (!ticking) {
        requestAnimationFrame(updateParallax);
        ticking = true;
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    updateParallax();
  }

  /**
   * Header is always solid — no scroll toggle
   */
  function initHeader() {}

  /**
   * Mobile menu — slide + fade
   */
  function initMobileMenu() {
    const menu = document.querySelector('[data-mobile-menu]');
    const toggle = document.querySelector('[data-mobile-menu-toggle]');
    if (!menu || !toggle) return;

    const closeButtons = menu.querySelectorAll('[data-mobile-menu-close]');
    const panel = menu.querySelector('.mobile-menu__panel');

    function setOpen(isOpen) {
      menu.classList.toggle('mobile-menu--open', isOpen);
      menu.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
      toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      toggle.setAttribute('aria-label', isOpen ? 'Close menu' : 'Open menu');
      document.body.classList.toggle('menu-open', isOpen);
    }

    toggle.addEventListener('click', () => {
      setOpen(!menu.classList.contains('mobile-menu--open'));
    });

    closeButtons.forEach((button) => {
      button.addEventListener('click', () => setOpen(false));
    });

    panel?.querySelectorAll('a').forEach((link) => {
      link.addEventListener('click', () => setOpen(false));
    });

    window.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') setOpen(false);
    });
  }

  /**
   * Quick add — Shopify AJAX cart
   */
  function updateCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = String(count);
      el.hidden = count <= 0;
    });
  }

  function showQuickAddAdded(button) {
    button.textContent = '✓ Added';
    button.classList.add('product-card__quick-add--added');
    window.setTimeout(() => {
      button.textContent = '+';
      button.classList.remove('product-card__quick-add--added');
      button.disabled = false;
    }, 1000);
  }

  function initQuickAdd() {
    const buttons = document.querySelectorAll('[data-quick-add]');
    if (!buttons.length) return;

    buttons.forEach((button) => {
      button.addEventListener('click', async (event) => {
        event.preventDefault();
        event.stopPropagation();

        if (button.classList.contains('product-card__quick-add--added')) return;

        const variantId = button.dataset.variantId;
        if (!variantId) return;

        button.disabled = true;

        try {
          const response = await fetch(window.Shopify?.routes?.root
            ? `${window.Shopify.routes.root}cart/add.js`
            : '/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({ id: variantId, quantity: 1 }),
          });

          if (!response.ok) throw new Error('Add to cart failed');

          const cart = await fetch('/cart.js').then((res) => res.json());
          updateCartCount(cart.item_count);
          showQuickAddAdded(button);
        } catch (error) {
          button.disabled = false;
        }
      });
    });
  }

  /**
   * Demo quick add — matches React preview (local cart count only)
   */
  function initDemoQuickAdd() {
    const buttons = document.querySelectorAll('[data-demo-quick-add]');
    if (!buttons.length) return;

    buttons.forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();

        if (button.classList.contains('product-card__quick-add--added')) return;

        const countEl = document.querySelector('[data-cart-count]');
        const current = countEl && !countEl.hidden ? parseInt(countEl.textContent, 10) || 0 : 0;
        updateCartCount(current + 1);
        button.disabled = true;
        showQuickAddAdded(button);
      });
    });
  }

  /**
   * Shop product grid — client-side sort and wishlist
   */
  function getShopCardSize(index) {
    if (index < 2) return 'large';
    if (index < 5) return 'medium';
    return 'standard';
  }

  function applyShopCardSize(item, index) {
    const size = getShopCardSize(index);
    item.classList.remove(
      'shop-product-grid__item--large',
      'shop-product-grid__item--medium',
      'shop-product-grid__item--standard',
    );
    item.classList.add(`shop-product-grid__item--${size}`);

    const card = item.querySelector('.shop-product-card');
    if (!card) return;

    card.classList.remove(
      'shop-product-card--large',
      'shop-product-card--medium',
      'shop-product-card--standard',
    );
    card.classList.add(`shop-product-card--${size}`);
  }

  function initShopProductGrid() {
    const section = document.querySelector('[data-shop-product-grid]');
    if (!section) return;

    const grid = section.querySelector('[data-shop-product-grid-items]');
    if (!grid) return;

    const sortButtons = section.querySelectorAll('[data-shop-sort]');
    const filterButton = section.querySelector('[data-shop-filter]');

    sortButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const sort = button.getAttribute('data-shop-sort') || 'all';

        sortButtons.forEach((entry) => {
          const isActive = entry === button;
          entry.classList.toggle('shop-product-grid__sort-button--active', isActive);
          if (isActive) {
            entry.setAttribute('aria-current', 'true');
          } else {
            entry.removeAttribute('aria-current');
          }
        });

        const items = [...grid.querySelectorAll('.shop-product-grid__item')];
        const sorted = items.sort((a, b) => {
          const cardA = a.querySelector('[data-shop-product-card]');
          const cardB = b.querySelector('[data-shop-product-card]');
          if (!cardA || !cardB) return 0;

          const priceA = parseInt(cardA.getAttribute('data-price') || '0', 10);
          const priceB = parseInt(cardB.getAttribute('data-price') || '0', 10);
          const availableA = cardA.getAttribute('data-available') === 'true';
          const availableB = cardB.getAttribute('data-available') === 'true';
          const indexA = parseInt(cardA.getAttribute('data-index') || '0', 10);
          const indexB = parseInt(cardB.getAttribute('data-index') || '0', 10);

          switch (sort) {
            case 'price-asc':
              return priceA - priceB;
            case 'price-desc':
              return priceB - priceA;
            case 'availability':
              return Number(availableB) - Number(availableA);
            case 'newest':
              return indexB - indexA;
            default:
              return indexA - indexB;
          }
        });

        sorted.forEach((item, index) => {
          grid.appendChild(item);
          applyShopCardSize(item, index);
        });
      });
    });

    filterButton?.addEventListener('click', () => {
      const isOpen = filterButton.getAttribute('aria-expanded') === 'true';
      filterButton.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
    });
  }

  function initShopWishlist() {
    document.querySelectorAll('[data-shop-wishlist]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();

        const isActive = button.classList.toggle('shop-product-card__wishlist--active');
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    });
  }

  function initProductPage() {
    const page = document.querySelector('[data-product-page]');
    if (!page) return;

    initProductGallery(page);
    initProductVariants(page);
    initProductQuantity(page);
    initProductForm(page);
  }

  function initProductGallery(page) {
    const gallery = page.querySelector('[data-product-gallery]');
    if (!gallery) return;

    syncProductGalleryHeight(page);

    const mainImage = gallery.querySelector('[data-gallery-main-image], .product-gallery__main-image');
    const thumbs = [...gallery.querySelectorAll('[data-gallery-thumb]')];
    const prev = gallery.querySelector('[data-gallery-prev]');
    const next = gallery.querySelector('[data-gallery-next]');
    if (!mainImage || !thumbs.length) return;

    let activeIndex = 0;

    function setActive(index) {
      activeIndex = index;
      const thumb = thumbs[index];
      if (!thumb) return;

      thumbs.forEach((entry, entryIndex) => {
        const isActive = entryIndex === index;
        entry.classList.toggle('product-gallery__thumb--active', isActive);
        if (isActive) {
          entry.setAttribute('aria-current', 'true');
        } else {
          entry.removeAttribute('aria-current');
        }
      });

      const fullSrc = thumb.getAttribute('data-gallery-src');
      const img = thumb.querySelector('img');
      const nextSrc = fullSrc || img?.currentSrc || img?.src;
      if (nextSrc) {
        mainImage.src = nextSrc;
        if (fullSrc) {
          mainImage.removeAttribute('srcset');
          mainImage.removeAttribute('sizes');
        } else if (img?.srcset) {
          mainImage.srcset = img.srcset;
          mainImage.sizes = img.sizes || '';
        }
        mainImage.classList.remove('is-fading');
        void mainImage.offsetWidth;
        mainImage.classList.add('is-fading');
      }

      if (prev) prev.disabled = index === 0;
      if (next) next.disabled = index === thumbs.length - 1;
      thumb.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }

    thumbs.forEach((thumb, index) => {
      thumb.addEventListener('click', () => setActive(index));
    });

    prev?.addEventListener('click', () => setActive(Math.max(0, activeIndex - 1)));
    next?.addEventListener('click', () => setActive(Math.min(thumbs.length - 1, activeIndex + 1)));
    setActive(0);
  }

  function syncProductGalleryHeight(page) {
    const gallery = page.querySelector('[data-product-gallery]');
    const purchase = page.querySelector('.product-purchase');
    const frame = gallery?.querySelector('[data-gallery-main-frame]');
    const thumbList = gallery?.querySelector('[data-gallery-thumbs]');
    if (!gallery || !purchase || !frame) return;

    const desktopQuery = window.matchMedia('(min-width: 768px)');

    const sync = () => {
      if (!desktopQuery.matches) {
        frame.style.height = '';
        frame.style.minHeight = '';
        frame.style.maxHeight = '';
        frame.style.aspectRatio = '';
        if (thumbList) thumbList.style.maxHeight = '';
        return;
      }

      const scale = page.querySelector('.product-hero--tall-gallery') ? 1.42 : 1.12;
      const height = Math.round(purchase.offsetHeight * scale);
      frame.style.height = `${height}px`;
      frame.style.minHeight = `${height}px`;
      frame.style.maxHeight = `${height}px`;
      frame.style.aspectRatio = 'auto';
      if (thumbList) thumbList.style.maxHeight = `${height}px`;
    };

    const observer = new ResizeObserver(sync);
    observer.observe(purchase);
    desktopQuery.addEventListener('change', sync);
    sync();
  }

  function initProductVariants(page) {
    const form = page.querySelector('[data-product-form]');
    const variantButtons = [...page.querySelectorAll('[data-variant-option]')];
    const variantInput = form?.querySelector('[data-variant-id]');
    const productJson = page.querySelector('[data-product-json]');
    const priceValue = page.querySelector('[data-product-price-value]');
    const addButton = page.querySelector('[data-add-to-cart]');
    const mainImage = page.querySelector('[data-gallery-main-image], .product-gallery__main-image');
    if (!form || !variantButtons.length || !variantInput) return;

    const product = productJson ? JSON.parse(productJson.textContent) : null;

    function formatMoney(cents) {
      const currency = window.Shopify?.currency?.active || 'USD';
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency,
        }).format(cents / 100);
      } catch (error) {
        return `$${(cents / 100).toFixed(2)}`;
      }
    }

    function updateVariant(variantId) {
      if (!product) return;
      const variant = product.variants.find((entry) => String(entry.id) === String(variantId));
      if (!variant) return;

      if (priceValue) priceValue.textContent = formatMoney(variant.price);
      if (addButton) {
        addButton.disabled = !variant.available;
        addButton.textContent = variant.available
          ? window.theme?.strings?.addToCart || 'Add to Cart'
          : window.theme?.strings?.soldOut || 'Sold Out';
      }

      if (variant.featured_image && mainImage) {
        mainImage.src = variant.featured_image.src;
        mainImage.removeAttribute('srcset');
      }
    }

    variantButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const group = button.closest('[data-product-option]') || page;
        const groupButtons = [...group.querySelectorAll('[data-variant-option]')];

        groupButtons.forEach((entry) => {
          const isSelected = entry === button;
          entry.classList.toggle('product-purchase__variant--selected', isSelected);
          entry.classList.toggle('product-purchase__size--selected', isSelected);
          entry.setAttribute('aria-selected', isSelected ? 'true' : 'false');
        });

        const variantId = button.getAttribute('data-variant-id') || variantInput.value;
        variantInput.value = variantId;
        updateVariant(variantId);
      });
    });
  }

  function initProductQuantity(page) {
    const wrapper = page.querySelector('[data-product-quantity]');
    if (!wrapper) return;

    const input = wrapper.querySelector('[data-quantity-input]');
    const minus = wrapper.querySelector('[data-quantity-minus]');
    const plus = wrapper.querySelector('[data-quantity-plus]');
    if (!input || !minus || !plus) return;

    minus.addEventListener('click', () => {
      input.value = String(Math.max(1, parseInt(input.value, 10) - 1 || 1));
    });

    plus.addEventListener('click', () => {
      input.value = String(Math.max(1, parseInt(input.value, 10) + 1 || 2));
    });
  }

  function formatCartMoney(cents) {
    const currency = window.Shopify?.currency?.active || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency,
      }).format((Number(cents) || 0) / 100);
    } catch (error) {
      return `$${((Number(cents) || 0) / 100).toFixed(2)}`;
    }
  }

  function initCartQuantities() {
    const page = document.querySelector('[data-cart-page]');
    const form = page?.querySelector('.cart-page__form') || document.querySelector('.cart-page__form');
    if (!page || !form) return;

    const cartRoot = window.Shopify?.routes?.root || '/';
    let syncing = false;
    const pending = new Map();

    function getItems() {
      return [...form.querySelectorAll('[data-cart-item]')];
    }

    function paintOptimistic() {
      let total = 0;
      getItems().forEach((itemEl) => {
        const unit = parseInt(itemEl.getAttribute('data-unit-price'), 10) || 0;
        const input = itemEl.querySelector('[data-quantity-input]');
        const qty = Math.max(0, parseInt(input?.value, 10) || 0);
        const lineTotal = unit * qty;
        total += lineTotal;
        const priceEl = itemEl.querySelector('[data-cart-line-price]');
        if (priceEl) priceEl.textContent = formatCartMoney(lineTotal);
      });
      const subtotal = form.querySelector('[data-cart-subtotal]');
      if (subtotal) subtotal.textContent = formatCartMoney(total);
    }

    function renderEmptyCart() {
      const inner = page.querySelector('.cart-page__inner') || page;
      form.remove();
      updateCartCount(0);
      if (inner.querySelector('[data-cart-empty]')) return;
      inner.insertAdjacentHTML(
        'beforeend',
        `<div class="catalog-empty" data-cart-empty>
          <p class="catalog-empty__message type-body-lg">${window.theme?.strings?.cartEmpty || 'Your cart is empty.'}</p>
          <a class="btn btn--primary catalog-empty__cta" href="${cartRoot}collections/all">Continue shopping</a>
        </div>`,
      );
    }

    function applyCart(cart) {
      updateCartCount(cart.item_count || 0);
      if (!cart.item_count) {
        renderEmptyCart();
        return;
      }

      const byKey = new Map((cart.items || []).map((entry) => [String(entry.key), entry]));
      getItems().forEach((itemEl) => {
        const key = String(itemEl.getAttribute('data-key') || '');
        const match = byKey.get(key);
        if (!match) {
          itemEl.remove();
          return;
        }
        itemEl.setAttribute('data-unit-price', String(match.final_price ?? match.price ?? 0));
        const input = itemEl.querySelector('[data-quantity-input]');
        if (input && document.activeElement !== input) input.value = String(match.quantity);
        const priceEl = itemEl.querySelector('[data-cart-line-price]');
        if (priceEl) priceEl.textContent = formatCartMoney(match.final_line_price ?? match.line_price ?? 0);
      });

      const subtotal = form.querySelector('[data-cart-subtotal]');
      if (subtotal) subtotal.textContent = formatCartMoney(cart.total_price ?? cart.items_subtotal_price ?? 0);
      getItems().forEach((itemEl, index) => itemEl.setAttribute('data-line', String(index + 1)));
    }

    async function flushUpdates() {
      if (syncing) return;
      syncing = true;
      try {
        while (pending.size) {
          const batch = new Map(pending);
          pending.clear();
          const updates = {};
          batch.forEach((qty, key) => {
            updates[key] = qty;
          });

          const response = await fetch(`${cartRoot}cart/update.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ updates }),
          });

          if (!response.ok) {
            for (const [key, qty] of batch.entries()) {
              const itemEl = getItems().find((el) => el.getAttribute('data-key') === key);
              const line = itemEl ? getItems().indexOf(itemEl) + 1 : 0;
              if (!line) continue;
              const changeRes = await fetch(`${cartRoot}cart/change.js`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ line, quantity: qty }),
              });
              if (!changeRes.ok) throw new Error('Cart change failed');
              applyCart(await changeRes.json());
            }
            continue;
          }

          applyCart(await response.json());
        }
      } catch (error) {
        console.error(error);
        try {
          applyCart(await fetch(`${cartRoot}cart.js`).then((res) => res.json()));
        } catch (fetchError) {
          console.error(fetchError);
        }
      } finally {
        syncing = false;
        if (pending.size) flushUpdates();
      }
    }

    function queueItemUpdate(itemEl, quantity) {
      const key = itemEl.getAttribute('data-key');
      if (!key) return;
      const qty = Math.max(0, parseInt(quantity, 10) || 0);
      const input = itemEl.querySelector('[data-quantity-input]');
      if (input) input.value = String(qty);
      paintOptimistic();
      pending.set(key, qty);
      flushUpdates();
    }

    page.addEventListener('click', (event) => {
      const minus = event.target.closest('[data-quantity-minus]');
      const plus = event.target.closest('[data-quantity-plus]');
      const remove = event.target.closest('[data-cart-remove]');
      if (!minus && !plus && !remove) return;
      const itemEl = event.target.closest('[data-cart-item]');
      if (!itemEl || !form.contains(itemEl)) return;
      event.preventDefault();
      event.stopPropagation();
      if (remove) {
        queueItemUpdate(itemEl, 0);
        return;
      }
      const input = itemEl.querySelector('[data-quantity-input]');
      const current = parseInt(input?.value, 10) || 0;
      queueItemUpdate(itemEl, plus ? current + 1 : Math.max(0, current - 1));
    });

    page.addEventListener('change', (event) => {
      const input = event.target.closest('[data-quantity-input]');
      if (!input) return;
      const itemEl = input.closest('[data-cart-item]');
      if (!itemEl || !form.contains(itemEl)) return;
      queueItemUpdate(itemEl, input.value);
    });

    page.addEventListener('input', (event) => {
      const input = event.target.closest('[data-quantity-input]');
      if (!input) return;
      const itemEl = input.closest('[data-cart-item]');
      if (!itemEl || !form.contains(itemEl)) return;
      paintOptimistic();
    });
  }

  function initProductForm(page) {
    const form = page.querySelector('[data-product-form]');
    if (!form) return;

    const paypalButton = form.querySelector('[data-buy-paypal]');
    if (paypalButton) {
      paypalButton.addEventListener('click', async () => {
        const variantInput = form.querySelector('[data-variant-id]');
        const quantityInput = form.querySelector('[data-quantity-input]');
        if (!variantInput) return;

        paypalButton.setAttribute('disabled', 'true');

        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: variantInput.value,
              quantity: parseInt(quantityInput?.value || '1', 10) || 1,
            }),
          });

          if (!response.ok) throw new Error('Add to cart failed');
          window.location.href = '/checkout';
        } catch (error) {
          console.error(error);
          paypalButton.removeAttribute('disabled');
        }
      });
    }

    form.addEventListener('submit', async (event) => {
      event.preventDefault();

      const variantInput = form.querySelector('[data-variant-id]');
      const quantityInput = form.querySelector('[data-quantity-input]');
      const button = form.querySelector('[data-add-to-cart]');
      if (!variantInput) return;

      button?.setAttribute('disabled', 'true');

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: variantInput.value,
            quantity: parseInt(quantityInput?.value || '1', 10) || 1,
          }),
        });

        if (!response.ok) throw new Error('Add to cart failed');

        const cart = await fetch('/cart.js').then((res) => res.json());
        document.querySelectorAll('[data-cart-count]').forEach((el) => {
          el.textContent = cart.item_count;
          el.hidden = cart.item_count === 0;
        });
      } catch (error) {
        console.error(error);
      } finally {
        button?.removeAttribute('disabled');
      }
    });
  }

  function initProductAccordion() {
    document.querySelectorAll('[data-product-accordion]').forEach((accordion) => {
      const items = [...accordion.querySelectorAll('[data-accordion-item]')];
      if (!items.length) return;

      const closeAll = (exceptItem) => {
        items.forEach((item) => {
          if (item === exceptItem) return;

          const trigger = item.querySelector('[data-accordion-trigger]');
          const panel = item.querySelector('[data-accordion-panel]');
          const icon = item.querySelector('.product-accordion__icon');

          item.classList.remove('product-accordion__item--open');
          trigger?.setAttribute('aria-expanded', 'false');
          panel?.classList.remove('product-accordion__panel--open');
          panel?.setAttribute('aria-hidden', 'true');
          icon?.classList.remove('product-accordion__icon--open');
        });
      };

      items.forEach((item) => {
        const trigger = item.querySelector('[data-accordion-trigger]');
        const panel = item.querySelector('[data-accordion-panel]');
        const icon = item.querySelector('.product-accordion__icon');
        if (!trigger || !panel) return;

        trigger.addEventListener('click', () => {
          const isOpen = item.classList.contains('product-accordion__item--open');

          if (isOpen) {
            item.classList.remove('product-accordion__item--open');
            trigger.setAttribute('aria-expanded', 'false');
            panel.classList.remove('product-accordion__panel--open');
            panel.setAttribute('aria-hidden', 'true');
            icon?.classList.remove('product-accordion__icon--open');
            return;
          }

          closeAll(item);
          item.classList.add('product-accordion__item--open');
          trigger.setAttribute('aria-expanded', 'true');
          panel.classList.add('product-accordion__panel--open');
          panel.setAttribute('aria-hidden', 'false');
          icon?.classList.add('product-accordion__icon--open');
        });
      });
    });
  }

  function initPredictiveSearch() {
    const toggle = document.querySelector('[data-predictive-search-toggle]');
    const panel = document.querySelector('[data-predictive-search]');
    const input = document.querySelector('[data-predictive-search-input]');
    const results = document.querySelector('[data-predictive-search-results]');
    const closeButtons = document.querySelectorAll('[data-predictive-search-close]');
    if (!toggle || !panel || !input || !results) return;

    let debounceId = 0;

    function renderResults(payload) {
      const products = payload?.resources?.results?.products || [];
      const collections = payload?.resources?.results?.collections || [];
      const pages = payload?.resources?.results?.pages || [];
      const items = [...products, ...collections, ...pages];

      if (!items.length) {
        results.innerHTML = `<p class="shop-search__empty">${window.theme?.strings?.searchNoResults || 'No results found.'}</p>`;
        results.hidden = false;
        return;
      }

      results.innerHTML = items
        .map((item) => {
          const title = item.title || '';
          const url = item.url || '#';
          const image = item.featured_image?.url || item.image || '';
          const type = item.object_type || item.type || 'result';
          return `
            <a class="shop-search__result" href="${url}">
              ${image ? `<img class="shop-search__result-image" src="${image}" alt="" loading="lazy" width="56" height="70">` : ''}
              <span class="shop-search__result-copy">
                <span class="shop-search__result-type">${type}</span>
                <span class="shop-search__result-title">${title}</span>
              </span>
            </a>
          `;
        })
        .join('');
      results.hidden = false;
    }

    async function fetchResults(term) {
      if (!term) {
        results.hidden = true;
        results.innerHTML = '';
        return;
      }

      const response = await fetch(
        `/search/suggest.json?q=${encodeURIComponent(term)}&resources[type]=product,collection,page&resources[limit]=8`,
      );
      if (!response.ok) return;
      const payload = await response.json();
      renderResults(payload);
    }

    function setOpen(isOpen) {
      panel.classList.toggle('shop-search--open', isOpen);
      panel.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
      toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      toggle.classList.toggle('site-header__icon--active', isOpen);
      document.body.classList.toggle('shop-search-open', isOpen);

      panel.querySelectorAll('button, input').forEach((element) => {
        element.tabIndex = isOpen ? 0 : -1;
      });

      if (isOpen) {
        input.focus();
        return;
      }

      input.value = '';
      results.hidden = true;
      results.innerHTML = '';
    }

    toggle.addEventListener('click', () => {
      setOpen(!panel.classList.contains('shop-search--open'));
    });

    closeButtons.forEach((button) => {
      button.addEventListener('click', () => setOpen(false));
    });

    input.addEventListener('input', () => {
      window.clearTimeout(debounceId);
      debounceId = window.setTimeout(() => fetchResults(input.value.trim()), 250);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && panel.classList.contains('shop-search--open')) {
        setOpen(false);
      }
    });
  }

  /**
   * Initialize on DOM ready
   */
  function init() {
    initScrollAnimations();
    initParallax();
    initHeader();
    initMobileMenu();
    initQuickAdd();
    initShopProductGrid();
    initShopWishlist();
    initPredictiveSearch();
    initProductPage();
    initProductAccordion();
    initCartQuantities();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
